import React, { useMemo, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";

import {
  ActionsSection,
  ButtonsSection,
  SubSection,
  DataSection,
} from "../../../../components/data-components/sections/DataSections";
import { SectionMenuItemsModel } from "../../../sewer-objects/section-details/SectionMenuItems";
import {
  DeleteInspection,
  EditInspection,
  ViewInspection,
  ViewInspectionFromAssignedProject,
  ManageDeletedData,
  EditInspectionFromAssignedProject,
  DeleteInspectionFromAssignedProject,
} from "../../../../services/authorization/Permissions";
import {
  DisplayInspectionName,
  DisplayInspectionRelevance,
  DisplayLinkedProject,
} from "../components/DataComponents";
import { DisplayDynamicProperties } from "../../../../components/data-components/DisplayDynamicProperties";
import EditButton from "../../../../components/buttons/edit-button/EditButton";
import DisplayDateCreated from "../../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../../components/data-components/DisplayCreatedBy";
import DeleteButton from "../../../../components/buttons/delete-button/DeleteButton";
import PageLayout from "../../../_layout/PageLayout";
import { CurrentSectionStorageModel } from "../../../../services/sewer-object/CurrentSectionStorage";
import useErrorHandler, {
  ApplicationError,
} from "../../../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import { CurrentInspectionStorageModel } from "../../../../services/inspection/CurrentInspectionStorage";
import { snackbarNotificationsService } from "../../../../services/notifications/SnackbarNotificationsService";
import useUrlParams from "../../../../services/url/UrlParamsService";
import { api } from "../../../../services/api/ApiService";
import { CollapsibleServiceModel } from "../../../../services/collapsible/CollapsibleService";
import { appSessionStorage } from "../../../../services/storage/SessionStorageService";
import GenericObjectIssues from "../../../../components/generic-object-issues/GenericObjectIssues";
import { translationService } from "../../../../services/translation/TranslationService";
import { navigatePath } from "../../../../helpers/StringHelpers";
import useRouteService from "../../../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../../../services/breadcrumbs/BreadcrumbsStorage";
import MarkRelevantButton from "../../../../components/buttons/relevant-outdated-buttons/MarkRelevantButton";
import MarkOutdatedButton from "../../../../components/buttons/relevant-outdated-buttons/MarkOutdatedButton";
import WarningBeforeAction from "../../../../components/data-components/WarningBeforeAction";
import useModal from "../../../../components/dialog/useModal";

export default observer(() => {
  const urlParamsService = useUrlParams();
  const history = useHistory();
  const { isUserOnProjects } = useRouteService();
  const { handleErrorInSnackbar, handleErrorInModal } = useErrorHandler();
  const { isVisible, toggleModal } = useModal();

  const [isDeleting, setIsDeleting] = useState(false);

  const [currentSectionStorage] = useState(() =>
    CurrentSectionStorageModel.create(
      {
        currentSectionId: urlParamsService.getSectionId()!,
        parentEntityFromPath: {
          id: urlParamsService.getSewerObjectParentId()!,
          type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job,
        },
      },
      { api }
    )
  );

  const currentInspectionId = urlParamsService.getInspectionId()!;

  const [currentInspectionStorage] = useState(() =>
    CurrentInspectionStorageModel.create({ currentInspectionId }, { api })
  );

  const collapsibleService = CollapsibleServiceModel.create(
    { collapsedContentKey: "genericIssuesInspectionIsCollapsed" },
    { appSessionStorage }
  );

  const getInspectionIsLockedWarning = useCallback(() => {
    if (
      currentInspectionStorage.userWithLockOnInspection &&
      !currentInspectionStorage.currentUserHasLockOnInspection
    )
      return {
        key: "ViewInspection.locked-inspection",
        context: { userName: currentInspectionStorage.userWithLockOnInspection.name },
      };
    else return undefined;
  }, [
    currentInspectionStorage.userWithLockOnInspection,
    currentInspectionStorage.currentUserHasLockOnInspection,
  ]);

  const getCurrentInspectionName = useCallback(
    () =>
      currentInspectionStorage.currentInspection.getName(
        currentSectionStorage.currentSection.inspectionsWithDates
      ),
    [
      currentInspectionStorage.currentInspection,
      currentSectionStorage.currentSection.inspectionsWithDates,
    ]
  );

  const menuItems = useMemo(() => {
    const sectionMenuItems = SectionMenuItemsModel.create({
      sectionId: currentSectionStorage.currentSectionId,
      section: currentSectionStorage.currentSection,
      parentIdFromPath: currentSectionStorage.parentEntityFromPath.id,
      shouldDisableMenuItems: isDeleting,
    });

    return {
      staticItems: sectionMenuItems.getStaticMenuItems(),
      dynamicItems: sectionMenuItems.getDynamicMenuItems(),
    };
  }, [
    currentSectionStorage.currentSectionId,
    currentSectionStorage.currentSection,
    currentSectionStorage.parentEntityFromPath.id,
    isDeleting,
  ]);

  const canUserDeleteInspection = useMemo(
    () =>
      !currentInspectionStorage.inspectionIsLockedBySomeoneElse &&
      permissionsService.userHasOneOfThePermissions([
        DeleteInspection,
        DeleteInspectionFromAssignedProject,
      ]),
    [currentInspectionStorage.inspectionIsLockedBySomeoneElse]
  );

  const canUserEditInspection = useMemo(
    () =>
      !currentInspectionStorage.inspectionIsLockedBySomeoneElse &&
      permissionsService.userHasOneOfThePermissions([
        EditInspection,
        EditInspectionFromAssignedProject,
      ]),
    [currentInspectionStorage.inspectionIsLockedBySomeoneElse]
  );

  const handleEditClick = () => {
    history.push(`${history.location.pathname}/edit`);
  };

  const handleDeleteInspection = useCallback(async () => {
    setIsDeleting(true);

    try {
      await currentInspectionStorage.validateIfInspectionCanBeDeleted();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withModalTitleKey("validation.inspection.relevance.deleteError.title");
      handleErrorInModal(applicationError);
      setIsDeleting(false);
      return;
    }

    try {
      await currentInspectionStorage.deleteInspection();
      snackbarNotificationsService.showSuccess("ViewInspection.delete.success");
      history.push(`${navigatePath(history.location.pathname, -1)}`);
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error.inspection.delete");
      handleErrorInSnackbar(applicationError);
    }

    setIsDeleting(false);
  }, [currentInspectionStorage, handleErrorInModal, handleErrorInSnackbar, history]);

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([
        {
          entity: currentSectionStorage.parentEntityFromPath.type,
          name: await currentSectionStorage.getParentEntityName(
            currentSectionStorage.currentSection.project.name
          ),
        },
        {
          entity: EntityTypeFromPath.SewerObject,
          name: currentSectionStorage.currentSection.name,
        },
        {
          entity: EntityTypeFromPath.Inspection,
          name: getCurrentInspectionName(),
        },
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [
    currentSectionStorage,
    currentSectionStorage.currentSection,
    getCurrentInspectionName,
    history.location.pathname,
  ]);

  const isMarkOutdatedButtonDisabled = useMemo(
    () =>
      currentInspectionStorage.currentInspection.lock !== null ||
      !permissionsService.userHasPermissionTo(EditInspection) ||
      (currentInspectionStorage.currentInspection.isRelevant &&
        currentSectionStorage.currentSection.inspectionsWithDates.length < 2),
    [
      currentInspectionStorage.currentInspection.isRelevant,
      currentInspectionStorage.currentInspection.lock,
      currentSectionStorage.currentSection.inspectionsWithDates.length,
    ]
  );

  const confirmMarkRelevantAction = useCallback(
    async (shouldAlsoMarkSameDateInspectionsAsRelevant: boolean) => {
      try {
        await currentInspectionStorage.markInspectionAsRelevant(
          shouldAlsoMarkSameDateInspectionsAsRelevant
        );
        await currentInspectionStorage.load();
        snackbarNotificationsService.showSuccess(
          "general.inspection.relevance.mark-relevant.success.snackbar"
        );

        if (isVisible) toggleModal();
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withModalTitleKey("ViewInspection.toggle-relevance.error.title");
        handleErrorInModal(applicationError);
      }
    },
    [currentInspectionStorage, handleErrorInModal, isVisible, toggleModal]
  );

  const onMarkRelevantButtonClick = useCallback(async () => {
    const inspectionsWithSameGatheringDate = currentInspectionStorage.currentInspection
      .gatheringDate
      ? currentSectionStorage.getOtherOutdatedInspectionsWithSameGatheringDate(
          currentInspectionStorage.currentInspection
        )
      : [];

    if (inspectionsWithSameGatheringDate.length > 0) {
      toggleModal();
      return;
    }

    await confirmMarkRelevantAction(false);
  }, [
    confirmMarkRelevantAction,
    currentInspectionStorage.currentInspection,
    currentSectionStorage,
    toggleModal,
  ]);

  const onMarkOutdatedButtonClick = useCallback(async () => {
    try {
      await currentInspectionStorage.markInspectionAsOutdated();
      await currentInspectionStorage.load();
      snackbarNotificationsService.showSuccess(
        "general.inspection.relevance.mark-outdated.success.snackbar"
      );
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withModalTitleKey("ViewInspection.toggle-relevance.error.title");
      handleErrorInModal(applicationError);
    }
  }, [currentInspectionStorage, handleErrorInModal]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewInspection, ViewInspectionFromAssignedProject]}
      titleKey="ViewInspection.title"
      titleContext={{
        sectionName: currentSectionStorage.currentSection.name,
        inspectionName: getCurrentInspectionName(),
      }}
      id="view-sectionInspection"
      menuItems={menuItems}
      isLoading={currentInspectionStorage.isLoading}
    >
      <ActionsSection
        warningKey={getInspectionIsLockedWarning()?.key}
        warningContext={getInspectionIsLockedWarning()?.context}
      >
        <ButtonsSection>
          <EditButton disabled={!canUserEditInspection} onClick={handleEditClick} />
          <DeleteButton
            disabled={!canUserDeleteInspection}
            confirmationAction={handleDeleteInspection}
            confirmationModalTitleKey="ViewInspection.delete.confirmation.title"
            confirmationModalSubtitleKey="ViewInspection.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
          {currentInspectionStorage.currentInspection.isRelevant ? (
            <MarkOutdatedButton
              onClick={onMarkOutdatedButtonClick}
              disabled={isMarkOutdatedButtonDisabled}
            />
          ) : (
            <MarkRelevantButton
              onClick={onMarkRelevantButtonClick}
              disabled={
                currentInspectionStorage.currentInspection.lock !== null ||
                !permissionsService.userHasPermissionTo(EditInspection)
              }
            />
          )}
        </ButtonsSection>
      </ActionsSection>
      <GenericObjectIssues
        collapsibleService={collapsibleService}
        genericIssues={currentInspectionStorage.currentInspection.genericIssues}
      />
      <DataSection labelKey="general.inspections.details">
        <SubSection>
          <DisplayInspectionName
            name={`${translationService.translate(
              "general.inspection"
            )} ${getCurrentInspectionName()}`}
          />
          <DisplayLinkedProject project={currentInspectionStorage.currentInspection.projectName} />
          <DisplayInspectionRelevance
            isRelevant={currentInspectionStorage.currentInspection.isRelevant}
          />
        </SubSection>
        <SubSection>
          <DisplayDateCreated date={currentInspectionStorage.currentInspection.created} />
          <DisplayCreatedBy createdBy={currentInspectionStorage.currentInspection.createdBy.name} />
        </SubSection>
        <SubSection></SubSection>
      </DataSection>
      <DisplayDynamicProperties
        labelKey="general.characteristics"
        properties={currentInspectionStorage.currentInspection.getDynamicPropertiesForView()}
      />
      <WarningBeforeAction
        isVisible={isVisible}
        titleKey="general.inspection.relevance.mark-relevant.modal.title"
        subtitleKey={"general.inspection.mark-relevant.modal.subtitle"}
        toggleModal={toggleModal}
        buttonOk={{
          id: "mark-all-button",
          textKey: "general.inspection.mark-relevant.modal.mark-all-button",
          action: () => confirmMarkRelevantAction(true),
        }}
        additionalButton={{
          id: "mark-selection-button",
          textKey: "general.inspection.mark-relevant.modal.mark-selection-button",
          action: () => confirmMarkRelevantAction(false),
        }}
      />
    </PageLayout>
  );
});
